import { create } from "zustand";
import {
  getCurrentUser,
  signIn,
  signUp,
  fetchUserAttributes,
  confirmSignUp,
  updateUserAttribute,
  updatePassword,
  type AuthUser,
} from "aws-amplify/auth";
import { toast } from "sonner";
import { triggerEvent } from "@/lib/utils";
import mixpanel from "mixpanel-browser";

export type SignInUserParams = {
  email: string;
  password: string;
};

export type SignUpUserParams = {
  email: string;
  password: string;
  fullname: string;
};

export type SendConfirmationUserParams = {
  email: string;
  code: string;
};

export type User = {
  email: string;
  email_verified: boolean;
  name: string;
  sub: string;
  identities?: string;
  ["custom:isOnboarded"]?: boolean;
} & AuthUser;

export type UserState = {
  data: User | null;
  isLoading: boolean;
  error: Error | null;
  signUpSucceed: boolean;
  confirmed: boolean;
  userUpdated: boolean;
  userUpdating: boolean;
  codeSent: boolean;
  userUpdateError: Error | null;
  email?: string;
  isfetching: boolean;
};

export const userState = create<UserState>(() => ({
  data: null,
  isLoading: false,
  isfetching: false,
  error: null,
  signUpSucceed: false,
  confirmed: false,
  userUpdated: false,
  userUpdating: false,
  codeSent: false,
  userUpdateError: null,
}));

export const userIsInit = () => {
  return (
    JSON.stringify(userState.getState()) ===
    JSON.stringify(userState.getInitialState())
  );
};

export const getUserIsFetching = () =>
  userState.setState(() => ({
    isLoading: false,
    data: null,
    error: null,
    isfetching: true,
  }));
export const getUserIsloading = () =>
  userState.setState(() => ({
    isLoading: true,
    data: null,
    error: null,
    isfetching: false,
  }));
export const getUserIsError = (error: Error) =>
  userState.setState(() => ({
    error: error,
    data: null,
    isLoading: false,
    isfetching: false,
  }));
export const getUserIsSuccess = (user: User) =>
  userState.setState(() => ({
    data: user,
    isLoading: false,
    error: null,
    isfetching: false,
  }));

export const resetUser = () =>
  userState.setState(() => userState.getInitialState());

export const getUser = async () => {
  getUserIsloading();

  try {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();

    mixpanel.identify(attributes.email);
    mixpanel.people.set({
      $email: attributes.email,
      name: attributes.name,
    });

    // @ts-ignore
    cioanalytics.identify(attributes.email, {
      name: attributes.name,
      last_login_at: new Date(),
      language: navigator.language.split("-")[0],
    });

    mixpanel.people.set({
      last_login_at: new Date(),
      login_method: attributes?.identities ? "google" : "krunsh",
    });

    mixpanel.people.set_once({
      created_at: new Date(),
    });

    triggerEvent("Logged In", {
      email: attributes.email,
      name: attributes.name,
    });
    triggerEvent("App Opened");

    getUserIsSuccess({ ...attributes, ...user } as unknown as User);
  } catch (error) {
    getUserIsError(new Error(error as string));
  }
};

export const signInUser = async (params: SignInUserParams) => {
  getUserIsFetching();

  try {
    const { email, password } = params;

    const response = await signIn({
      username: email,
      password,
    });

    if (response.isSignedIn) {
      await getCurrentUser();
      const attributes = await fetchUserAttributes();

      getUserIsSuccess(attributes as unknown as User);
    } else {
      userState.setState({
        isLoading: false,
        signUpSucceed: true,
      });
    }
  } catch (error) {
    getUserIsError(new Error(error as string));
  }
};

export const signUpUser = async (params: SignUpUserParams) => {
  getUserIsFetching();

  try {
    const { email, password, fullname } = params;
    await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          name: fullname,
          email,
        },
      },
    });

    userState.setState(() => ({
      data: null,
      isLoading: false,
      error: null,
      signUpSucceed: true,
      email,
    }));
  } catch (error) {
    getUserIsError(new Error(error as string));
  }
};

export const confirmEmailUser = async ({
  email,
  code,
}: SendConfirmationUserParams) => {
  getUserIsloading();

  try {
    await confirmSignUp({
      username: email,
      confirmationCode: code,
    });

    triggerEvent("Email Verified");

    userState.setState(() => ({
      data: null,
      isLoading: false,
      error: null,
      signUpSucceed: false,
      confirmed: true,
    }));
  } catch (error) {
    getUserIsError(new Error(error as string));
  }
};

export type UpdateUserAttributesParams = {
  key: "email" | "name";
  value: string;
};

export const updateUserAttributes = async ({
  key,
  value,
}: UpdateUserAttributesParams) => {
  userState.setState({ userUpdating: true });
  try {
    const response = await updateUserAttribute({
      userAttribute: {
        attributeKey: key,
        value,
      },
    });

    if (response.isUpdated) {
      getUserIsSuccess({
        ...(userState.getState().data as User),
        [key]: value,
      });

      userState.setState({ userUpdated: true, userUpdating: false });
    }

    if (
      !response.isUpdated &&
      response.nextStep.updateAttributeStep === "CONFIRM_ATTRIBUTE_WITH_CODE"
    ) {
      userState.setState({ codeSent: true, userUpdating: false });
    }
  } catch {
    userState.setState({ userUpdated: false, userUpdating: false });
    toast.error("Erreur lors de la mise à jour du profil.", {
      duration: 2000,
    });
  }
};

export const resetUserUpdateStatus = () => {
  userState.setState({
    codeSent: false,
    userUpdating: false,
    userUpdated: false,
    userUpdateError: null,
  });
};

export const setStatusToUserUpdated = (newStatus: boolean) => {
  userState.setState({ userUpdated: newStatus });
};

export const userUpdateEmailManually = (email: string) => {
  userState.setState({
    data: {
      ...(userState.getState().data as User),
      email,
    },
  });
};

export type UserUpdatePasswordInput = {
  oldPassword: string;
  newPassword: string;
};

export const userUpdatePassword = async ({
  oldPassword,
  newPassword,
}: UserUpdatePasswordInput) => {
  userState.setState({ userUpdating: true });

  try {
    await updatePassword({
      oldPassword,
      newPassword,
    });

    userState.setState({ userUpdated: true, userUpdating: false });

    triggerEvent("Password Reset Completed");
  } catch (e) {
    userState.setState({
      userUpdateError: new Error(e as string),
      userUpdating: false,
    });
  }
};
